import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SUBSCRIPTION_STATUS_LABELS,
  describePlatformAction,
  type PlatformPlanDetail,
  type SubscriberDetail,
  type SubscriberSummary,
} from '@azko/shared';
import { Link } from 'react-router-dom';
import { ApiRequestError, api } from '../../lib/api.js';
import { formatCurrency, maskDocument } from '../../lib/format.js';
import { useSession } from '../../lib/session.js';
import {
  Alert,
  Button,
  Card,
  CONTROL_CLASS,
  EmptyState,
  LoadingRows,
  Pagination,
  SearchInput,
  StatusBadge,
  cx,
  resetting,
  useCursorPages,
} from '../../components/ui.js';
import { useDebouncedValue } from '../../lib/useDebouncedValue.js';
import { SubscriberOverrides } from './SubscriberOverrides.js';

/**
 * Assinantes: a lista de contas da plataforma e o que a Azko pode fazer nelas.
 *
 * A tela responde na ordem em que o suporte precisa: quem esta com problema
 * primeiro (conta cortada no topo), depois a busca, e o detalhe so quando
 * alguem escolhe uma conta. Carregar detalhe de todas antecipadamente seria
 * varrer faturas e log de auditoria de milhares de condominios para exibir
 * doze linhas.
 *
 * Cortar acesso derruba o sistema inteiro do cliente — porteiro no balcao
 * incluido. Por isso a acao pede motivo, mostra o que vai acontecer e nao fica
 * a um clique de distancia na lista.
 */

function statusTone(status: SubscriberSummary['status']) {
  // Pendente pede acao de alguem — merece destaque, nao o cinza de inativo.
  if (status === 'pending_approval') return 'warn';
  return status === 'active' ? 'accent' : 'neutral';
}

function StatusLabel({ item }: { item: SubscriberSummary }) {
  const label =
    item.status === 'pending_approval'
      ? 'aguardando aprovacao'
      : item.status === 'suspended'
      ? 'acesso cortado'
      : item.status === 'canceled'
        ? 'cancelada'
        // Sem esta linha, conta recusada cairia no rotulo da ASSINATURA e
        // apareceria como "em teste" — a assinatura nasce `trialing` no signup
        // e a recusa nao a toca. A lista mostraria como cliente ativo
        // exatamente quem foi barrado na porta.
        : item.status === 'rejected'
          ? 'cadastro recusado'
          : item.subscriptionStatus
            ? SUBSCRIPTION_STATUS_LABELS[item.subscriptionStatus]
            : 'sem assinatura';

  return <StatusBadge tone={statusTone(item.status)}>{label}</StatusBadge>;
}

export function SubscribersPage() {
  const queryClient = useQueryClient();
  const { staff } = useSession();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState<
    'cut' | 'restore' | 'approve' | 'resend' | 'reject' | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [editandoCadastro, setEditandoCadastro] = useState(false);
  const [cadastro, setCadastro] = useState({ name: '', document: '', city: '', state: '' });
  const [novoPlano, setNovoPlano] = useState('');
  // Dois campos que NAO sao o `reason`: um o cliente le, o outro muda o
  // endereco de entrada. Ambos opcionais, ambos limpos junto com o resto.
  const [mensagemAoCliente, setMensagemAoCliente] = useState('');
  const [novoEmail, setNovoEmail] = useState('');

  const podeCortar = staff?.permissions.includes('plataforma:assinantes:cortar_acesso') ?? false;
  const podeLiberar = staff?.permissions.includes('plataforma:assinantes:liberar_acesso') ?? false;
  const podeAprovar = staff?.permissions.includes('plataforma:assinantes:aprovar') ?? false;
  const podeEditar = staff?.permissions.includes('plataforma:assinantes:editar') ?? false;
  const podeAbrirConta =
    staff?.permissions.includes('plataforma:condominios:visualizar') ?? false;

  const pages = useCursorPages();
  // Buscar volta para a primeira pagina: o cursor apontava para uma linha da
  // lista anterior e nao significa nada na lista filtrada.
  const changeSearch = resetting(pages, setSearch);

  /**
   * Sem debounce, cada tecla virava uma consulta com tres `LIKE '%...%'` mais
   * tres subselects correlacionados POR LINHA, sobre a tabela de condominios.
   * As telas do cliente ja faziam isso desde sempre; o backoffice era a unica
   * parte do produto que batia no banco a cada caractere.
   */
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin', 'assinantes', debouncedSearch, pages.cursor],
    queryFn: () =>
      api.get<{ items: SubscriberSummary[]; nextCursor: string | null; total: number }>(
        `/admin/assinantes?limit=25${
          debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''
        }${pages.cursor ? `&cursor=${encodeURIComponent(pages.cursor)}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });

  const { data: detail } = useQuery({
    queryKey: ['admin', 'assinante', selected],
    queryFn: () => api.get<SubscriberDetail>(`/admin/assinantes/${selected ?? ''}`),
    enabled: selected !== null,
  });

  function done() {
    setConfirming(null);
    setReason('');
    setMensagemAoCliente('');
    setNovoEmail('');
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['admin'] });
  }

  function onError(cause: unknown) {
    setError(cause instanceof ApiRequestError ? cause.message : 'Falha ao mudar o acesso.');
  }

  const cut = useMutation({
    mutationFn: () =>
      api.post(`/admin/assinantes/${selected ?? ''}/cortar-acesso`, {
        reason: reason.trim(),
      }),
    onSuccess: done,
    onError,
  });

  const restore = useMutation({
    mutationFn: () =>
      api.post(`/admin/assinantes/${selected ?? ''}/liberar-acesso`, {
        reason: reason.trim(),
      }),
    onSuccess: done,
    onError,
  });

  // Aprovar e a PRIMEIRA liberacao, nao a devolucao de um acesso cortado: ela
  // preenche `approved_at` e so aceita conta em `pending_approval`. Por isso
  // rota propria, e nao um "liberar acesso" reaproveitado.
  const approve = useMutation({
    mutationFn: () =>
      api.post(`/admin/assinantes/${selected ?? ''}/aprovar`, { reason: reason.trim() }),
    onSuccess: done,
    onError,
  });

  // Recusar: o outro desfecho da MESMA fila que o aprovar. Sem ela, despachar
  // um cadastro falso exigia aprova-lo primeiro — o que gera e ENVIA as
  // credenciais para quem esta sendo negado — e so entao cortar o acesso.
  const reject = useMutation({
    mutationFn: () =>
      api.post(`/admin/assinantes/${selected ?? ''}/recusar`, {
        reason: reason.trim(),
        ...(mensagemAoCliente.trim() ? { publicMessage: mensagemAoCliente.trim() } : {}),
      }),
    onSuccess: done,
    onError,
  });

  // Reenviar o acesso: gera senha NOVA e manda de novo por e-mail. Existe
  // porque e-mail falha calado — spam, endereco digitado errado no cadastro,
  // caixa cheia — e sem isto o cliente fica sem nenhum caminho de entrada,
  // porque nao ha reset self-service no produto.
  const resend = useMutation({
    mutationFn: () =>
      api.post(`/admin/assinantes/${selected ?? ''}/reenviar-acesso`, {
        reason: reason.trim(),
        ...(novoEmail.trim() ? { newEmail: novoEmail.trim() } : {}),
      }),
    onSuccess: done,
    onError,
  });

  // Catalogo para o seletor de plano. O filtro NAO exclui plano fechado de
  // proposito: mover uma conta para uma condicao legada e um dos motivos de
  // esta acao existir.
  const { data: planos } = useQuery({
    queryKey: ['admin', 'planos'],
    queryFn: () => api.get<{ items: PlatformPlanDetail[] }>('/admin/planos'),
    enabled: podeEditar,
  });

  const editar = useMutation({
    mutationFn: () => api.patch(`/admin/assinantes/${selected ?? ''}`, cadastro),
    onSuccess: () => {
      setEditandoCadastro(false);
      done();
    },
    onError,
  });

  const definirPlano = useMutation({
    mutationFn: () =>
      api.put(`/admin/assinantes/${selected ?? ''}/plano`, {
        planCode: novoPlano,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      setNovoPlano('');
      done();
    },
    onError,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <div>
        <p className="eyebrow">Plataforma</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Assinantes.</h1>
        <p className="mt-2 text-sm text-muted">
          {/* O total vem do BANCO, nao do tamanho da pagina. A frase antiga
              dizia "N condominios na plataforma" contando o que estava em maos
              — e o que estava em maos parava em 100, sem avisar. */}
          {total} {total === 1 ? 'condominio' : 'condominios'}
          {debouncedSearch ? ' nesta busca' : ' na plataforma'}
          {items.length < total && ` • mostrando ${items.length}`}
        </p>
      </div>

      <div className="max-w-md">
        <SearchInput
          value={search}
          onChange={(event) => changeSearch(event.target.value)}
          placeholder="Buscar por nome, cidade ou CNPJ"
        />
      </div>

      {error && <Alert>{error}</Alert>}
      {isLoading && <LoadingRows rows={4} />}

      {!isLoading && items.length === 0 && (
        <EmptyState
          title="Nenhum condominio encontrado"
          description="Ajuste a busca ou limpe o campo para ver a plataforma inteira."
        />
      )}

      {/* Fechada, a conta e um resumo de tres linhas — cabem duas por linha no
          monitor da equipe. Aberta, ela vira faturas + historico + acao com
          motivo, e ai ocupa a largura toda em vez de espremer duas colunas
          dentro de meia tela. */}
      <div className="grid items-start gap-4 xl:grid-cols-2">
        {items.map((item) => {
          const open = selected === item.id;

          return (
            <Card
              key={item.id}
              className={cx(
                'p-5 sm:p-6',
                item.status === 'suspended' && 'border-warn/40',
                open && 'xl:col-span-2',
              )}
            >
              <button
                type="button"
                className="flex w-full flex-wrap items-start justify-between gap-4 text-left"
                onClick={() => {
                  setSelected(open ? null : item.id);
                  setConfirming(null);
                  setReason('');
                  setError(null);
                }}
              >
                <div className="min-w-0">
                  <h2 className="text-xl font-bold tracking-tight">{item.name}</h2>
                  <p className="mt-1 text-sm text-muted">
                    {[item.city, item.state].filter(Boolean).join('/') || 'sem endereco'}
                    {' • '}
                    {item.planName ?? 'sem plano'}
                    {item.priceCents !== null && ` • ${formatCurrency(item.priceCents, 'BRL')}/mes`}
                  </p>
                  <p className="mt-2 text-sm text-faint tabular-nums">
                    {item.units} unidades • {item.residents} moradores • {item.packagesThisMonth}{' '}
                    encomendas no mes
                  </p>
                </div>
                <StatusLabel item={item} />
              </button>

              {open && (
                <div className="mt-5 border-t border-line-soft pt-5">
                  {/* Primeiro botao do painel, e de proposito: a maioria das
                      ligacoes se resolve LENDO a conta ("o aviso do 302 saiu?",
                      "qual o telefone do 101?"), nao mexendo nela. As acoes que
                      mudam o estado do cliente ficam la embaixo. */}
                  {podeAbrirConta && (
                    <Link to={`/condominios/${item.id}`}>
                      <Button variant="ghost" className="mb-5 py-1.5">
                        Abrir conta por dentro
                      </Button>
                    </Link>
                  )}

                  {!detail && <LoadingRows rows={2} />}

                  {detail && (
                    <>
                      <div className="grid gap-6 sm:grid-cols-2">
                        <div>
                          <p className="eyebrow">Faturas recentes</p>
                          {detail.invoices.length === 0 ? (
                            <p className="mt-3 text-sm text-faint">Nenhuma fatura emitida.</p>
                          ) : (
                            <ul className="mt-3 space-y-2">
                              {detail.invoices.map((invoice) => (
                                <li
                                  key={invoice.id}
                                  className="flex items-baseline justify-between gap-3 text-sm"
                                >
                                  <span className="tabular-nums">
                                    {formatCurrency(invoice.amountCents, 'BRL')}
                                  </span>
                                  <span
                                    className={cx(
                                      invoice.status === 'paid' ? 'text-muted' : 'text-warn',
                                    )}
                                  >
                                    {invoice.status}
                                    {invoice.dueAt &&
                                      ` • vence ${new Date(invoice.dueAt).toLocaleDateString('pt-BR')}`}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        <div>
                          <p className="eyebrow">O que a Azko ja fez aqui</p>
                          {detail.staffActions.length === 0 ? (
                            <p className="mt-3 text-sm text-faint">
                              Nenhuma acao da equipe nesta conta.
                            </p>
                          ) : (
                            <ul className="mt-3 space-y-2">
                              {detail.staffActions.map((entry, index) => (
                                <li key={`${entry.action}-${index}`} className="text-sm">
                                  <span className="font-semibold">
                                    {describePlatformAction(entry.action)}
                                  </span>
                                  <span className="text-faint">
                                    {' '}
                                    • {entry.actorName} •{' '}
                                    {new Date(entry.createdAt).toLocaleDateString('pt-BR')}
                                  </span>
                                  {entry.reason && (
                                    <span className="block text-faint">{entry.reason}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>

                      {/* A excecao vem logo depois do historico e ANTES das
                          acoes destrutivas: na maioria das ligacoes ("meu
                          limite acabou") ela e a resposta, e oferecer cortar
                          acesso antes de oferecer liberar limite inverte a
                          ordem do atendimento. */}
                      <SubscriberOverrides subscriberId={item.id} />

                      {/* Cadastro e plano ficam ACIMA do bloco de acoes: sao as
                          duas coisas que a equipe mexe no dia a dia, e o corte
                          de acesso — que derruba a portaria — nao deve ser o
                          que fica mais a mao. */}
                      {editandoCadastro && podeEditar && (
                        <div className="mt-6 grid gap-3 border-t border-line-soft pt-5 sm:grid-cols-2">
                          <label className="grid gap-1.5 text-sm sm:col-span-2">
                            <span className="font-semibold text-muted">Nome</span>
                            <input
                              value={cadastro.name}
                              onChange={(event) =>
                                setCadastro({ ...cadastro, name: event.target.value })
                              }
                              maxLength={160}
                              className={CONTROL_CLASS}
                            />
                          </label>
                          <label className="grid gap-1.5 text-sm">
                            <span className="font-semibold text-muted">CNPJ</span>
                            {/* Mascara na digitacao; o backend guarda so os
                                digitos, e e assim que a busca por CNPJ compara. */}
                            <input
                              value={maskDocument(cadastro.document)}
                              onChange={(event) =>
                                setCadastro({
                                  ...cadastro,
                                  document: event.target.value.replace(/\D/g, '').slice(0, 14),
                                })
                              }
                              inputMode="numeric"
                              placeholder="12.345.678/0001-90"
                              className={CONTROL_CLASS}
                            />
                          </label>
                          <label className="grid gap-1.5 text-sm">
                            <span className="font-semibold text-muted">Cidade / UF</span>
                            <div className="flex gap-2">
                              <input
                                value={cadastro.city}
                                onChange={(event) =>
                                  setCadastro({ ...cadastro, city: event.target.value })
                                }
                                className={CONTROL_CLASS}
                              />
                              <input
                                value={cadastro.state}
                                onChange={(event) =>
                                  setCadastro({
                                    ...cadastro,
                                    state: event.target.value.toUpperCase().slice(0, 2),
                                  })
                                }
                                maxLength={2}
                                className={cx(CONTROL_CLASS, 'w-20 shrink-0')}
                              />
                            </div>
                          </label>
                          <div className="flex flex-wrap gap-2 sm:col-span-2">
                            <Button
                              loading={editar.isPending}
                              disabled={cadastro.name.trim().length < 2}
                              onClick={() => editar.mutate()}
                            >
                              Salvar cadastro
                            </Button>
                            <Button variant="ghost" onClick={() => setEditandoCadastro(false)}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      )}

                      {podeEditar && !editandoCadastro && (
                        <div className="mt-6 border-t border-line-soft pt-5">
                          <p className="text-sm font-semibold text-muted">Definir plano</p>
                          <p className="mt-1 text-sm text-faint">
                            Aplica na hora, sem passar pela fila de solicitacao. A assinatura
                            atual e arquivada e uma nova nasce — o historico fica.
                          </p>
                          <div className="mt-3 flex flex-wrap items-end gap-2">
                            <select
                              value={novoPlano}
                              onChange={(event) => setNovoPlano(event.target.value)}
                              className={cx(CONTROL_CLASS, 'max-w-xs')}
                            >
                              <option value="">Escolha o plano</option>
                              {(planos?.items ?? [])
                                .filter((plan) => plan.code !== detail.planCode)
                                .map((plan) => (
                                  <option key={plan.code} value={plan.code}>
                                    {plan.name}
                                    {plan.isPublic ? '' : ' (fechado)'} —{' '}
                                    {formatCurrency(plan.priceCents, 'BRL')}
                                  </option>
                                ))}
                            </select>
                            <input
                              value={reason}
                              onChange={(event) => setReason(event.target.value)}
                              placeholder="Motivo (vai para o log)"
                              maxLength={255}
                              className={cx(CONTROL_CLASS, 'min-w-[16rem] flex-1')}
                            />
                            <Button
                              loading={definirPlano.isPending}
                              disabled={!novoPlano || reason.trim().length < 5}
                              onClick={() => definirPlano.mutate()}
                            >
                              Aplicar
                            </Button>
                          </div>
                        </div>
                      )}

                      {confirming ? (
                        <div className="mt-6 border-t border-line-soft pt-5">
                          {confirming === 'cut' && (
                            <Alert tone="warn">
                              Cortar o acesso derruba o sistema inteiro deste condominio — o
                              porteiro para de registrar e de entregar encomenda na hora em que voce
                              confirmar.
                            </Alert>
                          )}
                          {confirming === 'resend' && (
                            <Alert tone="warn">
                              Gera uma senha NOVA: a atual para de funcionar na hora e qualquer
                              sessao aberta do responsavel cai. Preenchendo o e-mail abaixo, o
                              acesso e MOVIDO para o novo endereco — o antigo deixa de entrar e
                              recebe um aviso.
                            </Alert>
                          )}
                          {confirming === 'approve' && (
                            <Alert tone="warn">
                              Aprovar libera o condominio para operar e so acontece uma vez — e ela
                              que carimba a data de aprovacao na conta. Confira o cadastro antes.
                            </Alert>
                          )}
                          {confirming === 'reject' && (
                            <Alert tone="warn">
                              Recusar fecha o cadastro para sempre: a conta nao pode ser aprovada
                              depois, e o responsavel recebe um e-mail avisando que a solicitacao
                              nao foi aprovada. O motivo abaixo NAO vai nesse e-mail — ele fica so
                              no log interno.
                            </Alert>
                          )}
                          <label
                            className="mt-4 block text-sm font-semibold text-muted"
                            htmlFor={`reason-${item.id}`}
                          >
                            Motivo — fica no log permanente da conta
                          </label>
                          <textarea
                            id={`reason-${item.id}`}
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            rows={2}
                            maxLength={255}
                            className={cx(CONTROL_CLASS, 'mt-1.5 resize-none')}
                          />
                          {confirming === 'reject' && (
                            <>
                              <label
                                className="mt-4 block text-sm font-semibold text-muted"
                                htmlFor={`publica-${item.id}`}
                              >
                                Mensagem ao cliente — opcional, vai no e-mail
                              </label>
                              <p className="mt-1 text-xs text-faint">
                                Deixe em branco e o e-mail diz apenas que a solicitacao nao foi
                                aprovada. O motivo acima nunca e enviado.
                              </p>
                              <textarea
                                id={`publica-${item.id}`}
                                value={mensagemAoCliente}
                                onChange={(event) => setMensagemAoCliente(event.target.value)}
                                rows={3}
                                maxLength={600}
                                placeholder="Ex: O CNPJ informado pertence a outra razao social."
                                className={cx(CONTROL_CLASS, 'mt-1.5 resize-none')}
                              />
                            </>
                          )}
                          {confirming === 'resend' && (
                            <>
                              <label
                                className="mt-4 block text-sm font-semibold text-muted"
                                htmlFor={`email-${item.id}`}
                              >
                                Novo e-mail de acesso — opcional
                              </label>
                              <p className="mt-1 text-xs text-faint">
                                Em branco, so a senha muda. Preenchido, o acesso passa a ser por
                                este endereco: mesmo usuario, mesmo historico.
                              </p>
                              <input
                                id={`email-${item.id}`}
                                type="email"
                                value={novoEmail}
                                onChange={(event) => setNovoEmail(event.target.value)}
                                maxLength={160}
                                autoComplete="off"
                                placeholder="sindico@condominio.com.br"
                                className={cx(CONTROL_CLASS, 'mt-1.5')}
                              />
                            </>
                          )}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button
                              loading={
                                cut.isPending ||
                                restore.isPending ||
                                approve.isPending ||
                                reject.isPending ||
                                resend.isPending
                              }
                              disabled={reason.trim().length < 5}
                              onClick={() =>
                                confirming === 'cut'
                                  ? cut.mutate()
                                  : confirming === 'approve'
                                    ? approve.mutate()
                                    : confirming === 'reject'
                                      ? reject.mutate()
                                      : confirming === 'resend'
                                        ? resend.mutate()
                                        : restore.mutate()
                              }
                            >
                              {confirming === 'cut'
                                ? 'Confirmar corte'
                                : confirming === 'approve'
                                  ? 'Aprovar conta'
                                  : confirming === 'reject'
                                    ? 'Recusar cadastro'
                                    : confirming === 'resend'
                                      ? novoEmail.trim()
                                        ? 'Mover acesso'
                                        : 'Gerar nova senha'
                                      : 'Confirmar liberacao'}
                            </Button>
                            <Button variant="ghost" onClick={() => setConfirming(null)}>
                              Voltar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-6 flex flex-wrap gap-3 border-t border-line-soft pt-5">
                          {detail.status === 'pending_approval' && podeAprovar && (
                            <>
                              <Button onClick={() => setConfirming('approve')}>
                                Aprovar conta
                              </Button>
                              {/* Fantasma, e nao destrutivo em vermelho: recusar e
                                  uma decisao normal da fila, nao um acidente. O
                                  peso da acao esta no aviso que aparece depois. */}
                              <Button variant="ghost" onClick={() => setConfirming('reject')}>
                                Recusar cadastro
                              </Button>
                            </>
                          )}
                          {podeEditar && (
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setCadastro({
                                  name: detail.name,
                                  document: '',
                                  city: detail.city,
                                  state: detail.state,
                                });
                                setEditandoCadastro(true);
                              }}
                            >
                              Editar cadastro
                            </Button>
                          )}
                          {detail.status === 'active' && podeAprovar && (
                            <Button variant="ghost" onClick={() => setConfirming('resend')}>
                              Senha ou e-mail de acesso
                            </Button>
                          )}
                          {detail.status === 'active' && podeCortar && (
                            <Button variant="ghost" onClick={() => setConfirming('cut')}>
                              Cortar acesso
                            </Button>
                          )}
                          {detail.status === 'suspended' && podeLiberar && (
                            <Button onClick={() => setConfirming('restore')}>Liberar acesso</Button>
                          )}
                          {detail.status === 'canceled' && (
                            <p className="text-sm text-faint">
                              Conta cancelada — reativar o contrato e outro fluxo.
                            </p>
                          )}
                          {detail.status === 'rejected' && (
                            <p className="text-sm text-faint">
                              Cadastro recusado — nao ha como aprovar depois. Se o cliente voltar
                              com os dados corrigidos, ele precisa se cadastrar de novo.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* A barra compartilhada, a mesma das listas do cliente. A condicao
          repete a do proprio `Pagination` porque aqui ela vive dentro de um
          Card — sem isso, uma lista de uma pagina ganharia uma caixa vazia no
          pe. */}
      {(pages.hasPrevious || data?.nextCursor) && (
        <Card>
          <Pagination
            pages={pages}
            nextCursor={data?.nextCursor ?? null}
            loading={isFetching}
            previousLabel="Anteriores"
            nextLabel="Proximos"
          />
        </Card>
      )}
    </>
  );
}
